import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Helper to check if URL is video
const isVideoUrl = (url) => {
  if (!url) return false;
  const ext = url.split('.').pop()?.toLowerCase().split('?')[0];
  return ['mp4', 'webm', 'mov', 'avi', 'mpeg'].includes(ext);
};

const HeroSlider = ({ pageContent = {} }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const videoRef = useRef(null);
  const sliderRef = useRef(null);
  
  // Mídias do Hero - pode ser configurado via admin
  const heroMedia = pageContent.heroImages && pageContent.heroImages.length > 0
    ? pageContent.heroImages
    : [
        'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&h=800&fit=crop',
        'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1920&h=800&fit=crop',
        'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1920&h=800&fit=crop'
      ];

  // Navegação
  const goToNext = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroMedia.length);
  }, [heroMedia.length]);

  const goToPrev = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroMedia.length) % heroMedia.length);
  }, [heroMedia.length]);

  // Auto-rotate slides (longer for videos)
  useEffect(() => {
    if (heroMedia.length <= 1 || isPaused) return;
    
    const currentMediaIsVideo = isVideoUrl(heroMedia[currentSlide]);
    const interval = currentMediaIsVideo ? 15000 : 5000; // 15s for videos, 5s for images
    
    const timer = setInterval(goToNext, interval);
    
    return () => clearInterval(timer);
  }, [heroMedia.length, currentSlide, heroMedia, isPaused, goToNext]);

  // Play video when it becomes current slide
  useEffect(() => {
    if (videoRef.current && isVideoUrl(heroMedia[currentSlide])) {
      videoRef.current.play().catch(() => {});
    }
  }, [currentSlide, heroMedia]);

  // Touch/Swipe handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsPaused(true);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsPaused(false);
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isSwipe = Math.abs(distance) > minSwipeDistance;
    
    if (isSwipe) {
      if (distance > 0) {
        goToNext(); // Swipe left -> next
      } else {
        goToPrev(); // Swipe right -> prev
      }
    }
    
    setIsPaused(false);
  };

  // Click handler for left/right areas
  const handleAreaClick = (e) => {
    if (heroMedia.length <= 1) return;
    
    const rect = sliderRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    
    // Se clicou no terço esquerdo, volta; se clicou no terço direito, avança
    if (clickX < width * 0.33) {
      goToPrev();
    } else if (clickX > width * 0.67) {
      goToNext();
    }
  };

  // Dynamic content with fallbacks
  const heroTitle = pageContent.heroTitle || 'Viaje pelo Brasil e pelo mundo, e crie suas melhores memórias';
  const heroSubtitle = pageContent.heroSubtitle || 'Turismo de aventura com segurança, profissionalismo e experiências inesquecíveis';
  const heroButtonText = pageContent.heroButtonText || 'Conheça nossos Pacotes';

  return (
    <div 
      ref={sliderRef}
      className="relative w-full h-[40vh] sm:h-[50vh] md:h-[500px] overflow-hidden mt-16 sm:mt-20 cursor-pointer select-none"
      onClick={handleAreaClick}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Background Images/Videos with Transition */}
      {heroMedia.map((media, index) => {
        const isVideo = isVideoUrl(media);
        const isActive = index === currentSlide;
        
        return isVideo ? (
          <video
            key={index}
            ref={isActive ? videoRef : null}
            src={media}
            autoPlay={isActive}
            muted
            loop
            playsInline
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              isActive ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ) : (
          <img
            key={index}
            src={media}
            alt={`Viaje pelo Brasil e pelo mundo ${index + 1}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              isActive ? 'opacity-100' : 'opacity-0'
            }`}
          />
        );
      })}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
      
      {/* Content */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-6 leading-tight">
            {heroTitle}
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-gray-200 mb-4 sm:mb-8 max-w-2xl mx-auto">
            {heroSubtitle}
          </p>
          <Link
            to="/pacotes"
            className="inline-block btn-primary-theme font-bold text-sm sm:text-base md:text-lg px-6 sm:px-10 py-2 sm:py-4 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg"
          >
            {heroButtonText}
          </Link>
        </div>
      </div>

      {/* Slide Indicators */}
      {heroMedia.length > 1 && (
        <>
          {/* Navigation Arrows (Desktop) */}
          <button
            onClick={(e) => { e.stopPropagation(); goToPrev(); }}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-2 sm:p-3 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 hover:opacity-100 focus:opacity-100 z-10"
            style={{ opacity: 0.7 }}
            aria-label="Anterior"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-2 sm:p-3 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 hover:opacity-100 focus:opacity-100 z-10"
            style={{ opacity: 0.7 }}
            aria-label="Próximo"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          
          {/* Dots Indicator */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
            {heroMedia.map((media, index) => {
              const isVideo = isVideoUrl(media);
              return (
                <button
                  key={index}
                  onClick={(e) => { e.stopPropagation(); setCurrentSlide(index); }}
                  className={`h-3 rounded-full transition-all duration-300 flex items-center justify-center ${
                    index === currentSlide 
                      ? 'bg-theme-secondary w-8' 
                      : 'bg-white/50 hover:bg-white/75 w-3'
                  }`}
                  title={isVideo ? 'Vídeo' : 'Imagem'}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default HeroSlider;
